import { useParams } from 'react-router-dom'
import {
  abrirAbaCarregando,
  ButtonType,
  direcionarAbaArquivo,
  DocumentoPessoaLabels,
  DocumentoPessoaType,
  fecharAba,
  generatePdfUrl,
  isIOS,
  MaskType,
  showAlert,
  showError,
} from '../../../../_metronic/helpers'
import { Button, FormInput, FormRadio, LoadingIndicator } from '../../../components'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import { useEffect, useRef, useState } from 'react'
import { ArquivoDto } from '../../apps/atendimento/core/_models'
import { downloadScreeningRelatorio } from './core/_request'

const validationSchema = Yup.object().shape({
  protocolo: Yup.string().required('O protocolo é obrigatório'),
  documento: Yup.string().required('O campo documento é obrigatório'),
  cpf: Yup.string().when('documento', {
    is: (tipo: DocumentoPessoaType) => tipo === DocumentoPessoaType.Cpf,
    then: Yup.string()
      .matches(/^\d{3}\.\d{3}\.\d{3}-\d{2}$/, 'CPF inválido')
      .required('O campo CPF é obrigatório'),
    otherwise: Yup.string().notRequired(),
  }),
  cns: Yup.string().when('documento', {
    is: (tipo: DocumentoPessoaType) => tipo === DocumentoPessoaType.Cns,
    then: Yup.string()
      .matches(/^\d{3}\s?\d{4}\s?\d{4}\s?\d{4}$/, 'CNS inválido')
      .required('O campo CNS é obrigatório'),
    otherwise: Yup.string().notRequired(),
  }),
})

export const DownloadPage = () => {
  const { protocolo, tipo } = useParams<{ protocolo?: string; tipo: 'screening' | 'aso' }>()

  const [pdfPath, setPdfPath] = useState('')
  const [isMobile, setIsMobile] = useState(false)
  // Handle da aba pré-aberta no gesto de toque (iOS); redirecionada após o await.
  const abaIOSRef = useRef<Window | null>(null)
  // Marca que há uma aba aguardando o onSubmit; se a validação falhar, o efeito
  // abaixo fecha a aba órfã.
  const aguardandoSubmitRef = useRef(false)

  const getRelatorio = async (tipo: string, protocolo: string, documento: string) => {
    switch (tipo) {
      case 'screening':
        return downloadScreeningRelatorio(protocolo, documento)

      /* case 'aso':
        return downloadAsoRelatorio(protocolo, documento) */

      default:
        throw new Error('Download inválido')
    }
  }

  const formik = useFormik({
    initialValues: {
      cpf: '',
      cns: '',
      documento: DocumentoPessoaType.Cpf,
      protocolo: protocolo || '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      aguardandoSubmitRef.current = false
      const aba = abaIOSRef.current
      try {
        if (isIOS() && !aba) {
          showAlert({
            title: 'Erro!',
            text: 'Não foi possível abrir o documento. Verifique se os pop-ups estão liberados e tente novamente.',
            icon: 'error',
          })
          return
        }
        const documento = values.documento === DocumentoPessoaType.Cpf ? values.cpf : values.cns
        const arquivo = await getRelatorio(tipo!, values.protocolo, documento)
        if (isIOS()) {
          direcionarAbaArquivo(aba, arquivo)
          abaIOSRef.current = null
        } else {
          setFile(arquivo)
        }
      } catch (error) {
        fecharAba(aba)
        abaIOSRef.current = null
        showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  const setFile = (file: ArquivoDto) => {
    if (file?.contentBase64) {
      const url = generatePdfUrl(file.contentBase64, file.type)
      if (url) {
        if (isMobile) window.open(url, '_blank')
        else setPdfPath(url)
      }
    }
  }

  // Fecha a aba órfã quando o usuário tenta enviar mas a validação falha (o
  // Formik não chama onSubmit nesse caso), evitando deixar aba em branco aberta.
  useEffect(() => {
    if (formik.submitCount > 0 && aguardandoSubmitRef.current && !formik.isSubmitting) {
      fecharAba(abaIOSRef.current)
      abaIOSRef.current = null
      aguardandoSubmitRef.current = false
    }
  }, [formik.submitCount, formik.isSubmitting])

  // Pré-abre a aba SÍNCRONA no clique do botão Enviar (antes da validação
  // assíncrona do Formik), preservando a user activation no iOS.
  const handleSubmitClick = () => {
    if (!isIOS()) return
    fecharAba(abaIOSRef.current)
    abaIOSRef.current = abrirAbaCarregando()
    aguardandoSubmitRef.current = true
  }

  useEffect(() => {
    const userAgent = navigator.userAgent || navigator.vendor
    // Reconhece iPadOS recente (UA "Macintosh" + toque) via isIOS().
    setIsMobile(/android/i.test(userAgent) || isIOS())
  }, [])

  return (
    <>
      {pdfPath ? (
        <object
          width='100%'
          height='450px'
          data={pdfPath + '#navpanes=0&scrollbar=0'}
          aria-label='Documento para download em PDF'
        >
          Documento para download em PDF
        </object>
      ) : (
        <form onSubmit={formik.handleSubmit}>
          <FormRadio
            formik={formik}
            name='documento'
            label='Documento'
            options={[
              {
                id: 1,
                descricao: 'CPF',
                value: DocumentoPessoaType.Cpf,
              },
              {
                id: 2,
                descricao: 'CNS',
                value: DocumentoPessoaType.Cns,
              },
            ]}
            className='mb-3'
            isRequired
          />
          {formik.values.documento === DocumentoPessoaType.Cpf ? (
            <FormInput
              formik={formik}
              key={formik.values.documento}
              label={DocumentoPessoaLabels[formik.values.documento]}
              name='cpf'
              placeholder='Digite seu CPF'
              mask={MaskType.CPF}
              className='mb-3'
              isRequired
            />
          ) : (
            <FormInput
              formik={formik}
              key={formik.values.documento}
              label={DocumentoPessoaLabels[formik.values.documento]}
              name='cns'
              placeholder='Digite seu CNS'
              mask={MaskType.CNS}
              className='mb-3'
              isRequired
            />
          )}
          <FormInput
            formik={formik}
            name='protocolo'
            label='Protocolo'
            placeholder='Digite o protocolo'
            className='mb-3'
            isRequired
          />
          <div className='d-flex flex-end'>
            <Button
              type={ButtonType.Submit}
              disabled={formik.isSubmitting}
              className='btn-primary'
              size='btn-sm'
              onClick={handleSubmitClick}
            >
              {formik.isSubmitting ? <LoadingIndicator /> : 'Enviar'}
            </Button>
          </div>
        </form>
      )}
    </>
  )
}
